import Store from '../Store';
import RestClient from 'shared/Network/RestClient';
import { toast } from 'react-toastify';
import { observable, action } from 'mobx';

class AccountsStoreClass extends Store {
    @observable accessor accounts = [];
    channels = ['Traditional Retail - Centralized', 'Drugstore', 'Organic Supermarket', 'Travel Retail', 'Online/Q-Commerce', 'Traditional Retail - Decentralized']
    listingTypes = ['Central Listing', 'Regional Listing', 'Decentral Listing', 'Online Listing']


    fetchData() {
        return new Promise(async (resolve, reject) => {
            if (this.status.fetched) {
                return resolve(this.list);
            }

            try {
                this.status.loading = true;

                const accounts = await this.fetchRoute('accountsoverview/accounts');
                const managers = await this.fetchRoute('accountsoverview/managers');
                const groups = await this.fetchRoute('accountsoverview/groups');

                this.status.fetched = true;

                this.list = {
                    accounts: accounts,
                    managers: managers.filter(x => !x.soft_deleted),
                    groups: groups.filter(x => !x.soft_deleted),
                }

                this.status.loading = false;
                resolve(this.list);

                this.taxCountries = accounts.filter(x => !x.soft_deleted).map(x => x.bc_tax_country).filter((v, i, a) => a.indexOf(v) === i);
                this.warehouseLocations = accounts.filter(x => !x.soft_deleted).map(x => x.bc_warehouse_location).filter((v, i, a) => a.indexOf(v) === i);
            } catch (error) {
                this.status.error = error.error?.message || error.data?.error || error.error || error.message || JSON.stringify(error) || 'An error occurred';
                this.status.loading = false;
                reject(error);
            }
        });
    }

    async fetchRoute(route = '') {
        const request = RestClient.prepareRequest('GET', route);

        try {
            const res = await request.send();

            if (res.success && 'data' in res) {
                return res.data?.value || res.data;
            } else {
                throw res.error?.message || res.data?.error || res.error || res.message || JSON.stringify(res) || 'An error occurred';
            }
        } catch (error) {
            toast.error(`Error fetching "${route}": ${error}`);
            return [];
        }
    }

    async createItem(item, entity) {
        const request = RestClient.prepareRequest('POST', `accountsoverview/${entity}`);
        request.setData(item);
        try {
            const response = await request.send();
            if (response.success) {
                if (entity === 'accounts') {
                    response.data = { ...item, ...response.data };
                    this.list.accounts.push(response.data);
                } else {
                    this.list[entity].push(response.data);
                }
            }
            return response.data;
        } catch (error) {
            toast.error(`Error fetching "${route}": ${error}`);
            return [];
        }
    }

    async updateAccount(account) {
        const item = this.list.accounts.find(x => x.id === account.id);
        console.log(item);
        const data = {
            channel: account.channel,
            email: account.email,
            listing_type: account.listing_type,
            pos_potential: account.pos_potential,
            wkz: account.wkz,
            discount: account.discount,
            forecast_revenue: account.forecast_revenue,
            switch_central_delivery: account.switch_central_delivery,
            customer_since: account.customer_since,
            groupId: account.AccountsOverviewGroupId,
            managerId: account.AccountsOverviewManagerId,
        }

        const request = RestClient.prepareRequest('PUT', 'accountsoverview/accounts/' + item.id);
        request.setData(data);

        const response = await request.send();

        if (response.success) {
            const index = this.list.accounts.indexOf(item);

            if (typeof this.list.accounts[index] === 'object') {
                for (const listKey of Object.keys(this.list.accounts[index])) {
                    if (listKey in response.data) {
                        this.list.accounts[index][listKey] = response.data[listKey];
                        if (listKey === 'AccountsOverviewGroupId') {
                            this.list.accounts[index].AccountsOverviewGroup = this.list.groups.find(x => x.id === response.data[listKey]);
                        }
                        if (listKey === 'AccountsOverviewManagerId') {
                            this.list.accounts[index].AccountsOverviewManager = this.list.managers.find(x => x.id === response.data[listKey]);
                        }
                    }
                }
            } else {
                this.list.accounts[index] = response.data;
            }
        }

        return response;
    }

    async deleteItem(item, entity) {
        let itemInList = this.list[entity].find(x => x.id === item.id);
        const request = RestClient.prepareRequest('DELETE', `accountsoverview/${entity}/${item.id}`);
        const response = await request.send();
        if (response.success) {
            if (entity === 'accounts') {
                itemInList.soft_deleted = true;
            } else {
                const index = this.list[entity].indexOf(itemInList);
                this.list[entity].splice(index, 1);
            }
        }
        return response.success;
    }

    @action
    async fetchAccountByNumber(number) {
        try {
            const response = await this.fetchRoute(`accountsoverview/accounts/${number}/bq`);
            return response;
        } catch (error) {
            toast.error(`Error fetching "${route}": ${error}`);
            return null;
        }
    }

    async revokeAccount(account) {
        const request = RestClient.prepareRequest('PUT', `accountsoverview/accounts/${account.id}/revoke`);
        try {
            const response = await request.send();
            if (response.success) {
                const index = this.list.accounts.findIndex(acc => acc.id === account.id);
                if (index !== -1) {
                    this.list.accounts[index].soft_deleted = false;
                }
            }
            return response;
        } catch (error) {
            toast.error(`Error revoking account: ${error}`);
            return null;
        }
    }

    async downloadFile(file) {
        if (!file) return { error: 'Error downloading file: Missing file' };

        const request = RestClient.prepareRequest('GET', `accountsoverview/accounts/download-file/${file?.id}`);

        try {
            const res = await RestClient.sendRequest(request, {
                responseType: 'arraybuffer',
                getResponseObject: true,
            });
            
            return res.data;
        } catch (error) {
            return error;
        }
    }

    async uploadFiles(id, files) {
        if (!id || !files) return { error: 'Error uploading file: Missing id or files' };

        const formData = new FormData();
        for (const file of files) formData.append('files', file);

        const request = RestClient.prepareRequest('POST', `accountsoverview/accounts/${id}/files`);
        request.setData(formData);

        try {
            const res = await RestClient.sendRequest(request);
            return res;
        } catch (error) {
            return error;
        }
    }

    async deleteFile(file) {
        if (!file) return { error: 'Error deleting file: Missing file' };

        const request = RestClient.prepareRequest('DELETE', `accountsoverview/accounts/file/${file?.id}`);

        try {
            const res = await RestClient.sendRequest(request);
            return res;
        } catch (error) {
            return error;
        }
    }
}

const AccountsStore = new AccountsStoreClass();

export default AccountsStore;